import React, { useState, useContext, useEffect } from 'react'
import { User, Product, Campaign } from 'app/types/authorization'
import { useTranslation } from 'next-i18next'
import { ButtonLoader } from '../components/old/Loaders'
import { CheckoutResponse, CheckoutSession } from 'app/types/checkout'
import { CheckoutImage, CompanyLogo } from '../components/old/Image'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { clientGetIdToken } from 'app/utils/cognito'
import { StripeCheckoutForm } from '../components/Checkout'
import { CompanyData, CompanyProfile } from 'app/types/organization'
import { capitalize, truncateDescription } from 'app/utils/text'
import { AuthContext } from 'app/utils/context'
import { captureException } from '@sentry/nextjs'
import { TFunction } from 'next-i18next'
import Currency from 'app/types/currency'
import { StandardAPIRequest } from 'app/types/api'
import {
  formatPrice,
  formatWithPercentage,
  priceInCents,
} from 'app/utils/price'
import { formatTimeLeft } from 'app/utils/time'

const refetchPage = async () => {
  window.location.reload()
}

// Interface for the Billing component props
interface BillingProps {
  user: User | undefined
  checkoutSession: CheckoutResponse
  eventSk: string
  organization: string
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  setCheckoutSession: React.Dispatch<
    React.SetStateAction<CheckoutResponse | undefined>
  >
  items: Product[]
  currency: Currency
  campaigns: Campaign[]
  companyProfile: CompanyProfile | null
  companyData: CompanyData[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => Promise<void>
  existingName?: string
  existingEmail?: string
  existingPhoneNumber?: string
  poolId?: string
  shippingMethod?: 'pickup' | 'shipping'
}

// Component for input field
const InputField: React.FC<{
  id: string
  label: string
  value: string
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onBlur?: () => void
  error?: string
  type?: string
  optional?: boolean
  t: TFunction
}> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  optional = false,
  t,
}) => (
  <div className="sm:col-span-2">
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label} {optional && `(${t('optional')})`}
    </label>
    <div className="mt-1">
      <input
        type={type}
        id={id}
        name={id}
        autoComplete={id}
        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {error && <p className="mt-1 text-red-500">{error}</p>}
    </div>
  </div>
)

// Component for the order summary
export const OrderSummary: React.FC<{
  items: Product[]
  currency: Currency
  checkoutSession: CheckoutResponse
  totalVAT: number
  ticketVAT: number
  itemVAT: number
  kioskVAT: number
  restaurantVAT: number
  t: TFunction
}> = ({
  items,
  currency,
  checkoutSession,
  totalVAT,
  ticketVAT,
  itemVAT,
  kioskVAT,
  restaurantVAT,
  t,
}) => (
  <section
    aria-labelledby="summary-heading"
    className="z-10 max-h-full w-full min-w-fit rounded-md bg-white text-black"
  >
    <div className="w-full rounded-md bg-white">
      <h2 id="summary-heading" className="sr-only">
        {t('order-summary')}
      </h2>

      <div className="flex flex-row justify-between rounded-t border border-gray-200 bg-gray-100 py-2 text-center text-sm text-gray-500">
        <div className="ml-4 flex w-1/2 justify-start">
          <p>{t('product-checkout')}</p>
        </div>
        <div className="hidden w-1/4 justify-start sm:flex">
          <p>{t('VAT')}</p>
        </div>
        <div className="mr-4 flex w-1/6 min-w-fit justify-start">
          <p>{t('total-checkout')}</p>
        </div>
      </div>
      <ul
        role="list"
        className="divide-y divide-white divide-opacity-10 overflow-y-scroll text-sm font-medium"
      >
        {items?.map((product) => {
          if (product.Ticket !== undefined) {
            return (
              <li
                key={`${product.Ticket.pk}-${product.Ticket.sk}`}
                className="flex flex-col items-center"
              >
                <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                  <div className="flex w-2/4 flex-col justify-start pl-4">
                    <h3 className="text-base font-bold text-gray-500">
                      {t('tickets')} {product.Ticket.selected_price?.name}{' '}
                      {product.Ticket.section_name}
                    </h3>
                    <p className="text-gray-500">
                      {product.Ticket.row && (
                        <span className="inline">
                          {capitalize(t('row'))} {product.Ticket.row}
                        </span>
                      )}
                      {product.Ticket.seat && (
                        <span className="inline">
                          , {capitalize(t('seat'))} {product.Ticket.seat}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="px-3">
                    <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                      <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                        {`${formatWithPercentage(
                          product.Ticket.selected_price,
                          ticketVAT,
                          currency
                        )}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-1/6 flex-row justify-start">
                    <p className="flex-none text-base font-bold">
                      {formatPrice(product.Ticket.selected_price, currency)}
                    </p>
                  </div>
                </div>
                <hr className="h-0.5 w-full border border-gray-200" />
              </li>
            )
          }
          if (product.Item !== undefined) {
            return (
              <li
                key={`${product.Item.pk}-${product.Item.sk}`}
                className="flex flex-col items-center"
              >
                <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                  <div className="flex w-2/4 flex-col justify-start pl-4">
                    <h3 className="text-base font-bold text-gray-500">
                      {product.Item.name}
                    </h3>
                    {product.Item.selected_size && (
                      <p className="text-gray-500">
                        {t('size')}, {product.Item.selected_size?.name}
                      </p>
                    )}
                  </div>
                  <div className="px-3">
                    <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                      <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                        {`${formatWithPercentage(
                          product.Item.selected_price,
                          itemVAT,
                          currency
                        )}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-1/6 flex-row justify-start">
                    <p className="flex-none text-base font-bold ">
                      {formatPrice(product.Item.selected_price, currency)}
                    </p>
                  </div>
                </div>
                <hr className="h-0.5 w-full border border-gray-200" />
              </li>
            )
          }
          if (product.PublicPool !== undefined) {
            return (
              <li
                key={`${product.PublicPool.pk}-${product.PublicPool.sk}`}
                className="flex flex-col items-center"
              >
                <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                  <div className="flex w-2/4 flex-col justify-start pl-4">
                    <h3 className="text-base font-bold text-gray-500">
                      {product.PublicPool.name}
                    </h3>
                    {product.PublicPool.selected_price && (
                      <p className="text-gray-500">
                        {t('size')}, {product.PublicPool.selected_price?.name}
                      </p>
                    )}
                  </div>
                  <div className="px-3">
                    <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                      <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                        {`${formatWithPercentage(
                          product.PublicPool.selected_price,
                          itemVAT,
                          currency
                        )}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-1/6 flex-row justify-start">
                    <p className="flex-none text-base font-bold ">
                      {formatPrice(product.PublicPool.selected_price, currency)}
                    </p>
                  </div>
                </div>
                <hr className="h-0.5 w-full border border-gray-200" />
              </li>
            )
          }
          if (product.Kiosk !== undefined) {
            return (
              <li
                key={`${product.Kiosk.pk}-${product.Kiosk.sk}`}
                className="flex flex-col items-center"
              >
                <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                  <div className="flex w-2/4 flex-col justify-start pl-4">
                    <h3 className="text-base font-bold text-gray-500">
                      {product.Kiosk.name}
                    </h3>
                    <p className="text-gray-500">
                      {truncateDescription(product.Kiosk.description)}
                    </p>
                  </div>
                  <div className="px-3">
                    <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                      <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                        {`${formatWithPercentage(
                          product.Kiosk.selected_price,
                          kioskVAT,
                          currency
                        )}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-1/6 flex-row justify-start">
                    <p className="flex-none text-base font-bold">
                      {formatPrice(product.Kiosk.selected_price, currency)}
                    </p>
                  </div>
                </div>
                <hr className="h-0.5 w-full border border-gray-200" />
              </li>
            )
          }
          if (product.RestaurantBooking !== undefined) {
            return (
              <li
                key={`${product.RestaurantBooking.pk}-${product.RestaurantBooking.sk}`}
                className="flex flex-col items-center"
              >
                <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                  <div className="flex w-2/4 flex-col justify-start pl-4">
                    <h3 className="text-base font-bold text-gray-500">
                      {product.RestaurantBooking.name} -{' '}
                      {product.RestaurantBooking.slot.seats} {t('seats')} (
                      {product.RestaurantBooking.slot.time})
                    </h3>
                    <p className="text-gray-500"></p>
                  </div>
                </div>
                <hr className="h-0.5 w-full border border-gray-200" />
              </li>
            )
          }
          if (product.Restaurant !== undefined) {
            return (
              <li
                key={`${product.Restaurant.pk}-${product.Restaurant.sk}`}
                className="flex flex-col items-center"
              >
                <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                  <div className="flex w-2/4 flex-col justify-start pl-4">
                    <h3 className="text-base font-bold text-gray-500">
                      {product.Restaurant.name}
                    </h3>
                    <p className="text-gray-500"></p>
                  </div>
                  <div className="px-3">
                    <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                      <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                        {`${formatWithPercentage(
                          product.Restaurant.selected_price,
                          restaurantVAT,
                          currency
                        )}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-1/6 flex-row justify-start">
                    <p className="flex-none text-base font-bold">
                      {formatPrice(product.Restaurant.selected_price, currency)}
                    </p>
                  </div>
                </div>
                <hr className="h-0.5 w-full border border-gray-200" />
              </li>
            )
          }
        })}
      </ul>
      <div className="mb-20 rounded-b bg-gray-100 p-4 sm:mb-0 sm:p-5">
        <div className="flex w-full flex-col py-3">
          <div>
            <p className="text-base font-bold text-gray-500">
              {t('order_summary')}
            </p>
          </div>
          <div className="flex w-full flex-row justify-between py-2">
            <div>
              <p className="text-gray-500">{t('tax_excluded')}</p>
            </div>
            <div>
              <p className="text-black sm:mr-10">
                {`${(
                  ((checkoutSession.checkout_session.cart.total_price ?? 0) -
                    totalVAT) /
                  100
                ).toFixed(2)} ${currency.toString()}`}
              </p>
            </div>
          </div>
          <hr className="h-0.5 w-full border border-gray-200" />
          <div className="flex w-full flex-row justify-between py-2">
            <div>
              <p className="text-gray-500">{t('VAT')}</p>
            </div>
            <div>
              <p className="text-black sm:mr-10">
                {`${(totalVAT / 100).toFixed(2)} ${currency.toString()}`}
              </p>
            </div>
          </div>
          <hr className="h-0.5 w-full border border-gray-200" />
          <div className="flex w-full flex-row justify-between py-2">
            <div>
              <p className="text-base font-bold text-gray-500">{t('total')}</p>
            </div>
            <div>
              {(checkoutSession.checkout_session.cart.original_price ?? 0) >
              (checkoutSession.checkout_session.cart.total_price ?? 0) ? (
                <p className="font-bold text-black sm:mr-10">
                  <span className="mr-1 line-through">
                    {(
                      (checkoutSession.checkout_session.cart.original_price ??
                        0) / 100
                    ).toFixed(2)}
                  </span>
                  {(
                    (checkoutSession.checkout_session.cart.total_price ?? 0) /
                    100
                  ).toFixed(2)}{' '}
                  {currency.toString()}
                </p>
              ) : (
                <p className="font-bold text-black sm:mr-10">
                  {(
                    (checkoutSession.checkout_session.cart.total_price ?? 0) /
                    100
                  ).toFixed(2)}{' '}
                  {currency.toString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
)

type PaymentMethods = 'stripe' | 'paytrail'

type BillingSessionPayTrail = {
  message?: string
  checkout_session?: CheckoutSession
  payment_url?: string
}

export function Billing({
  checkoutSession,
  items,
  user,
  eventSk,
  organization,
  setShowFailureMessage,
  setCheckoutSession,
  companyProfile,
  companyData,
  existingName,
  existingEmail,
  existingPhoneNumber,
  poolId,
  shippingMethod = 'pickup',
}: BillingProps) {
  const { t } = useTranslation('common')
  const { state } = useContext(AuthContext)
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState<boolean>(false)
  const [finished, setFinished] = useState<boolean>(false)
  const [totalVAT, setTotalVAT] = useState<number>(0)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethods>('stripe')

  useEffect(() => {
    if (items.length > 0) {
      let ticketPrices = items
        ?.map(
          (item) =>
            priceInCents(item.Ticket?.selected_price) *
            ((companyData[0]?.tax_tickets ?? 0) /
              (1 + (companyData[0]?.tax_tickets ?? 0)))
        )
        .reduce((prev, sum) => sum + prev, 0)
      let itemPrices = items
        ?.map(
          (item) =>
            priceInCents(item.Item?.selected_price) *
            ((companyData[0]?.tax_items ?? 0) /
              (1 + (companyData[0]?.tax_items ?? 0)))
        )
        .reduce((prev, sum) => sum + prev, 0)
      let kioskPrices = items
        ?.map(
          (item) =>
            priceInCents(item.Kiosk?.selected_price) *
            ((companyData[0]?.tax_kiosk ?? 0) /
              (1 + (companyData[0]?.tax_kiosk ?? 0)))
        )
        .reduce((prev, sum) => sum + prev, 0)
      let restaurantPrices = items
        ?.map(
          (item) =>
            priceInCents(item.Restaurant?.selected_price) *
            ((companyData[0]?.tax_restaurant ?? 0) /
              (1 + (companyData[0]?.tax_restaurant ?? 0)))
        )
        .reduce((prev, sum) => sum + prev, 0)
      setTotalVAT(ticketPrices + itemPrices + kioskPrices + restaurantPrices)
    }
  }, [items, companyData])

  const [sameShippingAndBilling, setSameShippingAndBilling] =
    useState<boolean>(true)

  // State for form fields
  const [email, setEmail] = useState<string>(existingEmail ?? user?.email ?? '')
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>(
    existingPhoneNumber ?? user?.phone_number ?? ''
  )
  const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] =
    useState<string>('')
  const [shippingName, setShippingName] = useState<string>(
    user?.shipping_adress?.name ?? ''
  )
  const [shippingAddress, setShippingAddress] = useState<string>(
    user?.shipping_adress?.address ?? ''
  )
  const [shippingCity, setShippingCity] = useState<string>(
    user?.shipping_adress?.city ?? ''
  )
  const [shippingPostalCode, setShippingPostalCode] = useState<string>(
    user?.shipping_adress?.postal_code ?? ''
  )
  const [billingName, setBillingName] = useState<string>(
    existingName ?? user?.billing_address?.name ?? ''
  )
  const [billingAddress, setBillingAddress] = useState<string>(
    user?.billing_address?.address ?? ''
  )
  const [billingCity, setBillingCity] = useState<string>(
    user?.billing_address?.city ?? ''
  )
  const [billingPostalCode, setBillingPostalCode] = useState<string>(
    user?.billing_address?.postal_code ?? ''
  )

  // Validation functions
  const validEmail = (localEmail: string): boolean => localEmail.length > 0
  const validPhoneNumber = (localPhoneNumber: string): boolean =>
    localPhoneNumber.length > 0

  // Update functions
  const updateEmail = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value)
  const updatePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPhoneNumber(event.target.value)
  const updateShippingName = (event: React.ChangeEvent<HTMLInputElement>) =>
    setShippingName(event.target.value)
  const updateShippingAddress = (event: React.ChangeEvent<HTMLInputElement>) =>
    setShippingAddress(event.target.value)
  const updateShippingCity = (event: React.ChangeEvent<HTMLInputElement>) =>
    setShippingCity(event.target.value)
  const updateShippingPostalCode = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setShippingPostalCode(event.target.value)
  const updateBillingName = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBillingName(event.target.value)
  const updateBillingAddress = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBillingAddress(event.target.value)
  const updateBillingCity = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBillingCity(event.target.value)
  const updateBillingPostalCode = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setBillingPostalCode(event.target.value)

  // Check functions
  const checkEmail = () =>
    setEmailErrorMessage(validEmail(email) ? '' : t('invalid-email') ?? '')
  const checkPhoneNumber = () =>
    setPhoneNumberErrorMessage(
      validPhoneNumber(phoneNumber) ? '' : t('invalid-phone-number') ?? ''
    )

  const [cartTimeToDelete, setCartTimeToDelete] = useState<number | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  // Effect for cart time to delete
  useEffect(() => {
    if (
      checkoutSession.checkout_session.cart.time_to_delete !== null &&
      checkoutSession.checkout_session.cart.time_to_delete !== undefined
    ) {
      const currentTime = Math.floor(new Date().getTime() / 1000)
      const diff =
        checkoutSession.checkout_session.cart.time_to_delete - currentTime
      setCartTimeToDelete(diff > 0 ? diff : null)
      setTimeLeft(diff > 0 ? diff : null)
    }
  }, [checkoutSession.checkout_session.cart])

  // Effect for countdown timer
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((timeLeft) =>
        timeLeft !== null
          ? timeLeft > 0
            ? timeLeft - 1
            : timeLeft
          : cartTimeToDelete
      )
    }, 1000)

    return () => clearInterval(intervalId)
  }, [cartTimeToDelete])

  useEffect(() => {
    if (timeLeft === 0) {
      alert(t('time-has-run-out-the-ticket-has-been-released'))
      window.location.reload()
    }
  }, [timeLeft, t])

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    checkEmail()
    checkPhoneNumber()
    if (!validEmail(email) || !validPhoneNumber(phoneNumber)) return

    const idToken = await clientGetIdToken()
    try {
      setLoading(true)
      let headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Cart-Token': state.cartToken,
      }
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }
      if (poolId) {
        headers['Company-Pool-Token'] = poolId
      }
      if (paymentMethod === 'paytrail') {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing_session_paytrail`,
          {
            method: 'PUT',
            body: JSON.stringify({
              email,
              phone_number: phoneNumber,
              shipping_name: shippingName,
              shipping_address: shippingAddress,
              shipping_city: shippingCity,
              shipping_postal_code: shippingPostalCode,
              billing_name: sameShippingAndBilling ? shippingName : billingName,
              billing_address: sameShippingAndBilling
                ? shippingAddress
                : billingAddress,
              billing_city: sameShippingAndBilling
                ? shippingPostalCode
                : billingCity,
              billing_postal_code: sameShippingAndBilling
                ? shippingPostalCode
                : billingPostalCode,
              checkout_session_uuid:
                checkoutSession.checkout_session.uuid ?? undefined,
            }),
            headers,
          }
        )
        const { data }: StandardAPIRequest<BillingSessionPayTrail> =
          await response.json()
        if (data?.payment_url) {
          window.open(data.payment_url, '_blank')
        }
      } else {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing`,
          {
            method: 'PUT',
            body: JSON.stringify({
              email,
              phone_number: phoneNumber,
              shipping_name: shippingName,
              shipping_address: shippingAddress,
              shipping_city: shippingCity,
              shipping_postal_code: shippingPostalCode,
              billing_name: sameShippingAndBilling ? shippingName : billingName,
              billing_address: sameShippingAndBilling
                ? shippingAddress
                : billingAddress,
              billing_city: sameShippingAndBilling
                ? shippingPostalCode
                : billingCity,
              billing_postal_code: sameShippingAndBilling
                ? shippingPostalCode
                : billingPostalCode,
              checkout_session_uuid:
                checkoutSession.checkout_session.uuid ?? undefined,
            }),
            headers,
          }
        )
        await response.json()
        if (!stripe || !elements) {
          // Stripe.js has not yet loaded.
          // Make sure to disable form submission until Stripe.js has loaded.
          return
        }

        const result = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${process.env.NEXT_PUBLIC_HOST}/orders/complete?event_id=${eventSk}&organization=${organization}`,
          },
        })

        if (result.error) {
          setShowFailureMessage(result.error.message ?? '')
        } else {
          if (result.paymentIntent?.status === 'succeeded') {
            setFinished(true)
          }
        }
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="flex flex-col-reverse bg-white sm:flex-row sm:flex-nowrap">
        <div className="flex h-full w-full flex-row justify-center bg-white px-4 pb-20 sm:w-2/5 sm:px-20">
          {finished ? (
            <div className="my-14">
              <div className="h-20">
                <CompanyLogo imageLink={companyProfile?.logo ?? undefined} />
              </div>
              <div className="mt-8">
                <h2 className="text-3xl font-bold">
                  {t('thank-you-for-supporting', {
                    companyName: companyProfile?.name,
                  })}
                </h2>
                <p className="mt-4">{t('payment-successful-message')}</p>
                <p className="mt-2 text-xl font-bold">
                  {t('cheer-for-company', {
                    companyName: companyProfile?.name,
                  })}
                </p>
                <button
                  onClick={async () => {
                    refetchPage()
                  }}
                  style={{
                    backgroundColor:
                      companyProfile?.secondary_main &&
                      companyProfile.secondary_main,
                  }}
                  className="mt-5 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                >
                  {t('buy-again')}
                </button>
              </div>
            </div>
          ) : (
            <section
              aria-labelledby="payment-and-shipping-heading"
              className="flex w-full"
            >
              <h2 id="payment-and-shipping-heading" className="sr-only">
                {t('payment-and-shipping-details')}
              </h2>

              <form method="POST" onSubmit={submitForm}>
                {timeLeft !== null &&
                  timeLeft > 0 &&
                  checkoutSession.checkout_session.cart.items.filter(
                    (item) => item.Ticket !== undefined
                  ).length > 0 && (
                    <div>
                      <p className="mb-4 text-xs font-bold text-gray-900">
                        {`${t('time-left')}: ${formatTimeLeft(timeLeft, t)}`}
                      </p>
                    </div>
                  )}
                <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
                  {organization === '2pTWvRLrXsY3RdHYa7fwamWVE8e' && (
                    <React.Fragment>
                      {paymentMethod === 'stripe' ? (
                        <button
                          className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-medium text-gray-900 shadow-sm hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                          onClick={() => setPaymentMethod('paytrail')}
                          type="button"
                        >
                          {t('i-would-like-to-pay-with-paytrail')}
                        </button>
                      ) : (
                        <button
                          className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-medium text-gray-900 shadow-sm hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                          onClick={() => setPaymentMethod('stripe')}
                          type="button"
                        >
                          {t('i-would-like-to-pay-with-card')}
                        </button>
                      )}
                    </React.Fragment>
                  )}
                  {paymentMethod === 'stripe' && (
                    <StripeCheckoutForm
                      user={user}
                      checkoutSession={checkoutSession}
                      setCheckoutSession={setCheckoutSession}
                    />
                  )}
                  <div>
                    {paymentMethod === 'paytrail' ? (
                      <React.Fragment>
                        <h3
                          id="contact-heading"
                          className="mt-10 text-lg font-medium text-gray-900"
                        >
                          {t('pay-with-paytrail')}
                        </h3>
                        <p className="mt-4 text-gray-500">
                          {t('pay-with-paytrail-description')}
                        </p>
                      </React.Fragment>
                    ) : (
                      <h3
                        id="contact-heading"
                        className="mt-10 text-lg font-medium text-gray-900"
                      >
                        {t('contact-information')}
                      </h3>
                    )}
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <InputField
                        id="name"
                        label={t('name')}
                        value={shippingName}
                        onChange={updateShippingName}
                        t={t}
                      />
                      <InputField
                        id="email"
                        label={t('email')}
                        value={email}
                        onChange={updateEmail}
                        onBlur={checkEmail}
                        error={emailErrorMessage}
                        type="email"
                        t={t}
                      />
                      <InputField
                        id="phone_number"
                        label={t('phone-number')}
                        value={phoneNumber}
                        onChange={updatePhoneNumber}
                        onBlur={checkPhoneNumber}
                        error={phoneNumberErrorMessage}
                        type="tel"
                        t={t}
                      />
                    </div>
                  </div>
                  <div className="mt-10">
                    <h3
                      id="shipping-heading"
                      className="text-lg font-medium text-gray-900"
                    >
                      {t('shipping-information')}
                    </h3>

                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <InputField
                        id="address"
                        label={t('address')}
                        value={shippingAddress}
                        onChange={updateShippingAddress}
                        optional={shippingMethod === 'pickup'}
                        t={t}
                      />
                      <InputField
                        id="postal-code"
                        label={t('postal-code')}
                        value={shippingPostalCode}
                        onChange={updateShippingPostalCode}
                        optional={shippingMethod === 'pickup'}
                        t={t}
                      />
                      <InputField
                        id="city"
                        label={capitalize(t('city'))}
                        value={shippingCity}
                        onChange={updateShippingCity}
                        optional={shippingMethod === 'pickup'}
                        t={t}
                      />
                    </div>
                  </div>

                  <div className="mt-10">
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('billing-information')}
                    </h3>

                    <div className="mt-6 flex items-center">
                      <input
                        id="same-as-shipping"
                        name="same-as-shipping"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                        checked={sameShippingAndBilling}
                        onChange={() =>
                          setSameShippingAndBilling(!sameShippingAndBilling)
                        }
                      />
                      <div className="ml-2">
                        <label
                          htmlFor="same-as-shipping"
                          className="text-sm font-medium text-gray-900"
                        >
                          {t('same-as-shipping-information')}
                        </label>
                      </div>
                    </div>
                    {!sameShippingAndBilling && (
                      <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                        <InputField
                          id="billing-name"
                          label={t('name')}
                          value={billingName}
                          onChange={updateBillingName}
                          t={t}
                        />
                        <InputField
                          id="billing-address"
                          label={t('address')}
                          value={billingAddress}
                          onChange={updateBillingAddress}
                          optional
                          t={t}
                        />
                        <InputField
                          id="billing-postal-code"
                          label={t('postal-code')}
                          value={billingPostalCode}
                          onChange={updateBillingPostalCode}
                          optional
                          t={t}
                        />
                        <InputField
                          id="billing-city"
                          label={t('city')}
                          value={billingCity}
                          onChange={updateBillingCity}
                          optional
                          t={t}
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-10 flex justify-end border-t border-gray-200 pt-6">
                    <button
                      type="submit"
                      className="rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                    >
                      {loading ? <ButtonLoader /> : t('pay-now')}
                    </button>
                  </div>
                </div>
              </form>
            </section>
          )}
        </div>
        <div className="flex w-full flex-row items-center justify-center bg-white sm:w-3/5">
          <div className="relative flex h-full w-full justify-center">
            <div className="absolute inset-0 h-full w-full">
              <CheckoutImage
                relative
                imageLink={companyProfile?.checkout_image ?? undefined}
                alt={''}
              />
            </div>

            <div className="z-10 w-5/6 max-w-2xl">
              <div className="mb-3 mt-12 flex flex-row justify-start">
                <div>
                  <p
                    style={{
                      color:
                        companyProfile?.primary_main &&
                        companyProfile.primary_main,
                    }}
                    className="font-bold"
                  >
                    {t('your-order')}
                  </p>
                </div>
              </div>

              <OrderSummary
                items={items}
                currency={
                  checkoutSession.checkout_session.cart.currency ?? Currency.SEK
                }
                checkoutSession={checkoutSession}
                totalVAT={totalVAT}
                ticketVAT={companyData[0]?.tax_tickets ?? 0}
                itemVAT={companyData[0]?.tax_items ?? 0}
                kioskVAT={companyData[0]?.tax_kiosk ?? 0}
                restaurantVAT={companyData[0]?.tax_restaurant ?? 0}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export function BillingNoStripe({
  checkoutSession,
  items,
  user,
  eventSk,
  setShowFailureMessage,
  currency,
  companyProfile,
  existingName,
  existingEmail,
  existingPhoneNumber,
  poolId,
}: BillingProps) {
  const { state } = useContext(AuthContext)
  const { t } = useTranslation('common')
  const [loading, setLoading] = useState<boolean>(false)

  const [sameShippingAndBilling, setSameShippingAndBilling] =
    useState<boolean>(true)

  const totalPrice = 0

  const [email, setEmail] = useState<string>(existingEmail ?? user?.email ?? '')
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('')
  const validEmail = (localEmail: string): boolean => localEmail.length > 0
  const updateEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }
  const checkEmail = () =>
    setEmailErrorMessage(validEmail(email) ? '' : t('invalid-email') ?? '')

  const [phoneNumber, setPhoneNumber] = useState<string>(
    existingPhoneNumber ?? user?.phone_number ?? ''
  )
  const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] =
    useState<string>('')
  const validPhoneNumber = (localPhoneNumber: string): boolean =>
    localPhoneNumber.length > 0
  const updatePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value)
  }
  const checkPhoneNumber = () =>
    setPhoneNumberErrorMessage(
      validPhoneNumber(phoneNumber) ? '' : t('invalid-phone-number') ?? ''
    )

  const [shippingName, setShippingName] = useState<string>(
    existingName ?? user?.shipping_adress?.name ?? ''
  )
  const [shippingNameErrorMessage, setShippingNameErrorMessage] =
    useState<string>('')
  const validShippingName = (localShippingName: string): boolean =>
    localShippingName.length > 0
  const updateShippingName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShippingName(event.target.value)
  }
  const checkShippingName = () =>
    setShippingNameErrorMessage(
      validShippingName(shippingName) ? '' : t('invalid-name') ?? ''
    )

  const [shippingAddress, setShippingAddress] = useState<string>(
    user?.shipping_adress?.address ?? ''
  )
  const [shippingAddressErrorMessage, setShippingAddressErrorMessage] =
    useState<string>('')
  const validShippingAddress = (localShippingAddress: string): boolean =>
    localShippingAddress.length > 0
  const updateShippingAddress = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShippingAddress(event.target.value)
  }
  const checkShippingAddress = () =>
    setShippingAddressErrorMessage(
      validShippingAddress(shippingAddress) ? '' : t('invalid-address') ?? ''
    )

  const [shippingCity, setShippingCity] = useState<string>(
    user?.shipping_adress?.city ?? ''
  )
  const [shippingCityErrorMessage, setShippingCityErrorMessage] =
    useState<string>('')
  const validShippingCity = (localShippingCity: string): boolean =>
    localShippingCity.length > 0
  const updateShippingCity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShippingCity(event.target.value)
  }
  const checkShippingCity = () =>
    setShippingCityErrorMessage(
      validShippingCity(shippingCity) ? '' : t('invalid-city') ?? ''
    )

  const [shippingPostalCode, setShippingPostalCode] = useState<string>(
    user?.shipping_adress?.postal_code ?? ''
  )
  const [shippingPostalCodeErrorMessage, setShippingPostalCodeErrorMessage] =
    useState<string>('')
  const validShippingPostalCode = (localShippingPostalCode: string): boolean =>
    localShippingPostalCode.length > 0
  const updateShippingPostalCode = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShippingPostalCode(event.target.value)
  }
  const checkShippingPostalCode = () =>
    setShippingPostalCodeErrorMessage(
      validShippingPostalCode(shippingPostalCode)
        ? ''
        : t('invalid-postal-code') ?? ''
    )

  const [billingName, setBillingName] = useState<string>(
    user?.billing_address?.name ?? ''
  )
  const [billingNameErrorMessage, setBillingNameErrorMessage] =
    useState<string>('')
  const validBillingName = (localBillingName: string): boolean =>
    localBillingName.length > 0
  const updateBillingName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingName(event.target.value)
  }
  const checkBillingName = () =>
    setBillingNameErrorMessage(
      validBillingName(billingAddress) ? '' : t('invalid-name') ?? ''
    )

  const [billingAddress, setBillingAddress] = useState<string>(
    user?.billing_address?.address ?? ''
  )
  const [billingAddressErrorMessage, setBillingAddressErrorMessage] =
    useState<string>('')
  const validBillingAddress = (localBillingAddress: string): boolean =>
    localBillingAddress.length > 0
  const updateBillingAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingAddress(event.target.value)
  }
  const checkBillingAddress = () =>
    setBillingAddressErrorMessage(
      validBillingAddress(billingAddress) ? '' : t('invalid-address') ?? ''
    )

  const [billingCity, setBillingCity] = useState<string>(
    user?.billing_address?.city ?? ''
  )
  const [billingCityErrorMessage, setBillingCityErrorMessage] =
    useState<string>('')
  const validBillingCity = (localBillingCity: string): boolean =>
    localBillingCity.length > 0
  const updateBillingCity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingCity(event.target.value)
  }
  const checkBillingCity = () =>
    setBillingCityErrorMessage(
      validBillingCity(billingCity) ? '' : t('invalid-city') ?? ''
    )

  const [billingPostalCode, setBillingPostalCode] = useState<string>(
    user?.billing_address?.postal_code ?? ''
  )
  const [billingPostalCodeErrorMessage, setBillingPostalCodeErrorMessage] =
    useState<string>('')
  const validBillingPostalCode = (localBillingPostalCode: string): boolean =>
    localBillingPostalCode.length > 0
  const updateBillingPostalCode = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBillingPostalCode(event.target.value)
  }
  const checkBillingPostalCode = () =>
    setBillingPostalCodeErrorMessage(
      validBillingPostalCode(billingPostalCode) ? '' : 'Ej giltig'
    )

  const [finished, setFinished] = useState<boolean>(false)

  const [cartTimeToDelete, setCartTimeToDelete] = useState<number | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  // Effect for cart time to delete
  useEffect(() => {
    if (
      checkoutSession.checkout_session.cart.time_to_delete !== null &&
      checkoutSession.checkout_session.cart.time_to_delete !== undefined
    ) {
      const currentTime = Math.floor(new Date().getTime() / 1000)
      const diff =
        checkoutSession.checkout_session.cart.time_to_delete - currentTime
      setCartTimeToDelete(diff > 0 ? diff : null)
      setTimeLeft(diff > 0 ? diff : null)
    }
  }, [checkoutSession.checkout_session.cart])

  // Effect for countdown timer
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((timeLeft) =>
        timeLeft !== null
          ? timeLeft > 0
            ? timeLeft - 1
            : timeLeft
          : cartTimeToDelete
      )
    }, 1000)

    return () => clearInterval(intervalId)
  }, [cartTimeToDelete])

  useEffect(() => {
    if (timeLeft === 0) {
      alert(t('time-has-run-out-the-ticket-has-been-released'))
      window.location.reload()
    }
  }, [timeLeft, t])

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const idToken = await clientGetIdToken()
    let headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Cart-Token': state.cartToken,
    }
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`
    }
    if (poolId) {
      headers['Company-Pool-Token'] = poolId
    }
    try {
      setLoading(true)
      await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing_free`,
        {
          method: 'PUT',
          body: JSON.stringify({
            email,
            phone_number: phoneNumber,
            shipping_name: shippingName,
            shipping_address: shippingAddress,
            shipping_city: shippingCity,
            shipping_postal_code: shippingPostalCode,
            billing_name: sameShippingAndBilling ? shippingName : billingName,
            billing_address: sameShippingAndBilling
              ? shippingAddress
              : billingAddress,
            billing_city: sameShippingAndBilling
              ? shippingPostalCode
              : billingCity,
            billing_postal_code: sameShippingAndBilling
              ? shippingPostalCode
              : billingPostalCode,
            checkout_session_uuid:
              checkoutSession.checkout_session.uuid ?? undefined,
          }),
          headers,
        }
      )
      setFinished(true)
    } catch (err) {
      captureException(err)
      setShowFailureMessage('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="flex flex-col-reverse bg-white sm:flex-row sm:flex-nowrap">
        <div className="flex h-full w-full flex-row justify-center bg-white px-4 pb-20 sm:w-2/5 sm:px-20">
          {finished ? (
            <div className="my-14">
              <div className="h-20">
                <CompanyLogo imageLink={companyProfile?.logo ?? undefined} />
              </div>
              <div className="mt-8">
                <h2 className="text-3xl font-bold">
                  {t('thank-you-for-supporting', {
                    companyName: companyProfile?.name,
                  })}
                </h2>
                <p className="mt-4">
                  {t('payment-successful-order-confirmation')}
                </p>
                <p className="mt-2 text-xl font-bold">
                  {t('cheer-for')} {companyProfile?.name}!
                </p>
                <button
                  onClick={async () => {
                    refetchPage()
                  }}
                  style={{
                    backgroundColor:
                      companyProfile?.secondary_main &&
                      companyProfile.secondary_main,
                  }}
                  className="mt-5 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                >
                  {t('buy-again')}
                </button>
              </div>
            </div>
          ) : (
            <section
              aria-labelledby="payment-and-shipping-heading"
              className="flex"
            >
              <form method="POST" onSubmit={submitForm}>
                {timeLeft !== null &&
                  timeLeft > 0 &&
                  checkoutSession.checkout_session.cart.items.filter(
                    (item) => item.Ticket !== undefined
                  ).length > 0 && (
                    <div>
                      <p className="mb-4 text-xs font-bold text-gray-900">
                        {`${t('time-left')}: ${formatTimeLeft(timeLeft, t)}`}
                      </p>
                    </div>
                  )}
                <h2 id="payment-and-shipping-heading" className="sr-only">
                  {t('payment-and-shipping-details')}
                </h2>
                <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
                  <div>
                    <h2
                      id="shipping-heading"
                      className="mt-10 text-2xl font-bold text-gray-900"
                    >
                      {t('payment')}
                    </h2>
                    <h3
                      id="shipping-heading"
                      className="mt-8 text-base font-bold text-gray-900"
                    >
                      {t('contact-information')}
                    </h3>

                    <div className=" grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="name"
                          className="block text-sm font-bold text-gray-700"
                        >
                          {t('name')}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="name"
                            name="name"
                            autoComplete="name"
                            className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                            value={shippingName}
                            onChange={updateShippingName}
                            onBlur={checkShippingName}
                          />
                          {shippingNameErrorMessage.length > 0 && (
                            <p className="mt-1 text-sm text-red-500">
                              {shippingNameErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="email"
                          className="block text-sm font-bold text-gray-700"
                        >
                          {t('email')}
                        </label>
                        <div className="mt-3">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            autoComplete="email"
                            className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                            value={email}
                            onChange={updateEmail}
                            onBlur={checkEmail}
                          />
                          {emailErrorMessage.length > 0 && (
                            <p className="mt-1 text-sm text-red-500">
                              {emailErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="phone_number"
                          className="block text-xs font-bold text-gray-700"
                        >
                          {t('phone-number')}
                        </label>
                        <div className="mt-1">
                          <input
                            type="tel"
                            id="phone_number"
                            name="phone_number"
                            autoComplete="phone_number"
                            className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                            value={phoneNumber}
                            onChange={updatePhoneNumber}
                            onBlur={checkPhoneNumber}
                          />
                          {phoneNumberErrorMessage.length > 0 && (
                            <p className="mt-1 text-sm text-red-500">
                              {phoneNumberErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3
                      id="shipping-heading"
                      className="text-lg font-bold text-gray-900"
                    >
                      {t('shipping-information')}
                    </h3>

                    <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="address"
                          className="block text-sm font-bold text-gray-700"
                        >
                          {`${t('address')} (${t('optional')})`}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="address"
                            name="address"
                            autoComplete="street-address"
                            className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                            value={shippingAddress}
                            onChange={updateShippingAddress}
                            onBlur={checkShippingAddress}
                          />
                          {shippingAddressErrorMessage.length > 0 && (
                            <p className="mt-1 text-sm text-red-500">
                              {shippingAddressErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="postal-code"
                          className="block text-sm font-bold text-gray-700"
                        >
                          {`${t('postal-code')} (${t('optional')})`}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="postal-code"
                            name="postal-code"
                            autoComplete="postal-code"
                            className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                            value={shippingPostalCode}
                            onChange={updateShippingPostalCode}
                            onBlur={checkShippingPostalCode}
                          />
                          {shippingPostalCodeErrorMessage.length > 0 && (
                            <p className="mt-1 text-sm text-red-500">
                              {shippingPostalCodeErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="city"
                          className="block text-sm font-bold text-gray-700"
                        >
                          {`${t('city')} (${t('optional')})`}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="city"
                            name="city"
                            autoComplete="address-level2"
                            className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                            value={shippingCity}
                            onChange={updateShippingCity}
                            onBlur={checkShippingCity}
                          />
                          {shippingCityErrorMessage.length > 0 && (
                            <p className="mt-1 text-sm text-red-500">
                              {shippingCityErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10">
                    <h3 className="text-lg font-bold text-gray-900">
                      {t('billing-information')}
                    </h3>

                    <div className="mt-6 flex items-center">
                      <input
                        id="same-as-shipping"
                        name="same-as-shipping"
                        type="checkbox"
                        className="h-4 w-4 rounded-sm border-gray-300 text-gray-600 focus:ring-gray-500"
                        checked={sameShippingAndBilling}
                        onChange={() =>
                          setSameShippingAndBilling(!sameShippingAndBilling)
                        }
                      />
                      <div className="ml-2">
                        <label
                          htmlFor="same-as-shipping"
                          className="text-sm font-medium text-gray-900"
                        >
                          {t('same-as-shipping-information')}
                        </label>
                      </div>
                    </div>
                    {!sameShippingAndBilling && (
                      <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <label
                            htmlFor="name"
                            className="block text-sm font-bold text-gray-700"
                          >
                            {t('name')}
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="name"
                              name="name"
                              autoComplete="name"
                              className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                              value={billingName}
                              onChange={updateBillingName}
                              onBlur={checkBillingName}
                            />
                            {billingNameErrorMessage.length > 0 && (
                              <p className="mt-1 text-red-500">
                                {billingNameErrorMessage}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="sm:col-span-2">
                          <label
                            htmlFor="address"
                            className="block text-sm font-bold text-gray-700"
                          >
                            {`${t('address')} (${t('optional')})`}
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="address"
                              name="address"
                              autoComplete="street-address"
                              className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                              value={billingAddress}
                              onChange={updateBillingAddress}
                              onBlur={checkBillingAddress}
                            />
                            {billingAddressErrorMessage.length > 0 && (
                              <p className="mt-1 text-red-500">
                                {billingAddressErrorMessage}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="postal-code"
                            className="block text-sm font-bold text-gray-700"
                          >
                            {`${t('postal-code')} (${t('optional')})`}
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="postal-code"
                              name="postal-code"
                              autoComplete="postal-code"
                              className="block w-full rounded-s border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                              value={billingPostalCode}
                              onChange={updateBillingPostalCode}
                              onBlur={checkBillingPostalCode}
                            />
                            {billingPostalCodeErrorMessage.length > 0 && (
                              <p className="mt-1 text-red-500">
                                {billingPostalCodeErrorMessage}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="city"
                            className="block text-sm font-bold text-gray-700"
                          >
                            {`${t('city')} (${t('optional')})`}
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="city"
                              name="city"
                              autoComplete="address-level2"
                              className="block w-full rounded-sm border-gray-300 bg-gray-100 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                              value={billingCity}
                              onChange={updateBillingCity}
                              onBlur={checkBillingCity}
                            />
                            {billingCityErrorMessage.length > 0 && (
                              <p className="mt-1 text-red-500">
                                {billingCityErrorMessage}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-10 flex flex-col justify-between border-t border-gray-200 pt-6">
                    <button
                      type="submit"
                      className="rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                    >
                      {loading ? <ButtonLoader /> : t('finish-order')}
                    </button>
                  </div>
                </div>
              </form>
            </section>
          )}
        </div>
        <div className="flex w-full flex-row items-center justify-center bg-white sm:w-3/5">
          <div className="relative flex h-full w-full justify-center">
            <div className="absolute inset-0 h-full w-full">
              <CheckoutImage
                relative
                imageLink={companyProfile?.checkout_image ?? undefined}
                alt={''}
              />
            </div>

            <div className="z-10 w-5/6 max-w-2xl">
              <div className="mb-3 mt-12 flex flex-row justify-start">
                <div>
                  <p className="font-bold text-gray-900">{t('your-order')}</p>
                </div>
              </div>

              <section
                aria-labelledby="summary-heading"
                className="z-10 max-h-full w-full min-w-fit rounded-md bg-white text-black"
              >
                <div className="w-full rounded-md bg-white">
                  <h2 id="summary-heading" className="sr-only">
                    {t('order-summary')}
                  </h2>

                  <div className="flex flex-row justify-between rounded-t border border-gray-200 bg-gray-100 py-2 text-center text-sm text-gray-500">
                    <div className="ml-4 flex w-1/2 justify-start">
                      <p>{t('product-checkout')}</p>
                    </div>
                    <div className="hidden w-1/4 justify-start sm:flex">
                      <p>{t('VAT')}</p>
                    </div>
                    <div className="mr-4 flex w-1/6 min-w-fit justify-start">
                      <p>{t('total-checkout')}</p>
                    </div>
                  </div>

                  <ul
                    role="list"
                    className="divide-y divide-white divide-opacity-10 overflow-y-scroll text-sm font-medium"
                  >
                    {items?.map((product) => {
                      if (product.Ticket !== undefined) {
                        return (
                          <li
                            key={`${product.Ticket.pk}-${product.Ticket.sk}`}
                            className="flex flex-col items-center"
                          >
                            <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                              <div className="flex w-2/4 flex-col justify-start pl-4">
                                <h3 className="text-base font-bold text-gray-500">
                                  {t('tickets')}{' '}
                                  {product.Ticket.selected_price?.name}{' '}
                                  {product.Ticket.section_name}
                                </h3>
                                <p className="text-gray-500">
                                  {product.Ticket.row && (
                                    <span className="inline">
                                      {capitalize(t('row'))}{' '}
                                      {product.Ticket.row}
                                    </span>
                                  )}
                                  {product.Ticket.seat && (
                                    <span className="inline">
                                      , {capitalize(t('seat'))}{' '}
                                      {product.Ticket.seat}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="px-3">
                                <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                                  <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                                    1
                                  </div>
                                </div>
                              </div>
                              <div className="flex w-1/6 flex-row justify-start">
                                <p className="flex-none text-base font-bold">
                                  {formatPrice(
                                    product.Ticket.selected_price,
                                    currency
                                  )}
                                </p>
                              </div>
                            </div>
                            <hr className="h-0.5 w-full border border-gray-200" />
                          </li>
                        )
                      }
                      if (product.Item !== undefined) {
                        return (
                          <li
                            key={`${product.Item.pk}-${product.Item.sk}`}
                            className="flex flex-col items-center"
                          >
                            <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                              <div className="flex w-2/4 flex-col justify-start pl-4">
                                <h3 className="text-base font-bold text-gray-500">
                                  {product.Item.name}
                                </h3>
                                {product.Item.selected_size && (
                                  <p className="text-gray-500">
                                    {t('size')},{' '}
                                    {product.Item.selected_size?.name}
                                  </p>
                                )}
                              </div>
                              <div className="px-3">
                                <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                                  <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                                    1
                                  </div>
                                </div>
                              </div>
                              <div className="flex w-1/6 flex-row justify-start">
                                <p className="flex-none text-base font-bold ">
                                  {formatPrice(
                                    product.Item.selected_price,
                                    currency
                                  )}
                                </p>
                              </div>
                            </div>
                            <hr className="h-0.5 w-full border border-gray-200" />
                          </li>
                        )
                      }
                      if (product.PublicPool !== undefined) {
                        return (
                          <li
                            key={`${product.PublicPool.pk}-${product.PublicPool.sk}`}
                            className="flex flex-col items-center"
                          >
                            <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                              <div className="flex w-2/4 flex-col justify-start pl-4">
                                <h3 className="text-base font-bold text-gray-500">
                                  {product.PublicPool.name}
                                </h3>
                                {product.PublicPool.selected_price && (
                                  <p className="text-gray-500">
                                    {t('size')},{' '}
                                    {product.PublicPool.selected_price?.name}
                                  </p>
                                )}
                              </div>
                              <div className="px-3">
                                <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                                  <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                                    1
                                  </div>
                                </div>
                              </div>
                              <div className="flex w-1/6 flex-row justify-start">
                                <p className="flex-none text-base font-bold ">
                                  {formatPrice(
                                    product.PublicPool.selected_price,
                                    currency
                                  )}
                                </p>
                              </div>
                            </div>
                            <hr className="h-0.5 w-full border border-gray-200" />
                          </li>
                        )
                      }
                      if (product.Kiosk !== undefined) {
                        return (
                          <li
                            key={`${product.Kiosk.pk}-${product.Kiosk.sk}`}
                            className="flex flex-col items-center"
                          >
                            <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                              <div className="flex w-2/4 flex-col justify-start pl-4">
                                <h3 className="text-base font-bold text-gray-500">
                                  {product.Kiosk.name}
                                </h3>
                                <p className="text-gray-500">
                                  {truncateDescription(
                                    product.Kiosk.description
                                  )}
                                </p>
                              </div>
                              <div className="px-3">
                                <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                                  <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                                    1
                                  </div>
                                </div>
                              </div>
                              <div className="flex w-1/6 flex-row justify-start">
                                <p className="flex-none text-base font-bold">
                                  {formatPrice(
                                    product.Kiosk.selected_price,
                                    currency
                                  )}
                                </p>
                              </div>
                            </div>
                            <hr className="h-0.5 w-full border border-gray-200" />
                          </li>
                        )
                      }
                      if (product.RestaurantBooking !== undefined) {
                        return (
                          <li
                            key={`${product.RestaurantBooking.pk}-${product.RestaurantBooking.sk}`}
                            className="flex flex-col items-center"
                          >
                            <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                              <div className="flex w-2/4 flex-col justify-start pl-4">
                                <h3 className="text-base font-bold text-gray-500">
                                  {product.RestaurantBooking.name} -{' '}
                                  {product.RestaurantBooking.slot.seats}{' '}
                                  {t('seats')} (
                                  {product.RestaurantBooking.slot.time})
                                </h3>
                                <p className="text-gray-500"></p>
                              </div>
                            </div>
                            <hr className="h-0.5 w-full border border-gray-200" />
                          </li>
                        )
                      }
                      if (product.Restaurant !== undefined) {
                        return (
                          <li
                            key={`${product.Restaurant.pk}-${product.Restaurant.sk}`}
                            className="flex flex-col items-center"
                          >
                            <div className=" flex w-full flex-row items-center justify-start gap-2 py-4 sm:justify-between sm:gap-0">
                              <div className="flex w-2/4 flex-col justify-start pl-4">
                                <h3 className="text-base font-bold text-gray-500">
                                  {product.Restaurant.name}
                                </h3>
                                <p className="text-gray-500"></p>
                              </div>
                              <div className="px-3">
                                <div className="flex w-full flex-row items-center rounded-md bg-gray-50">
                                  <div className="flex w-1/2 cursor-pointer flex-row justify-center rounded-l py-1">
                                    1
                                  </div>
                                </div>
                              </div>
                              <div className="flex w-1/6 flex-row justify-start">
                                <p className="flex-none text-base font-bold">
                                  {formatPrice(
                                    product.Restaurant.selected_price,
                                    currency
                                  )}
                                </p>
                              </div>
                            </div>
                            <hr className="h-0.5 w-full border border-gray-200" />
                          </li>
                        )
                      }
                    })}
                  </ul>
                  <div className="mb-20 rounded-b bg-gray-100 p-4 sm:mb-0 sm:p-5">
                    <div className="flex w-full flex-col py-3">
                      <div>
                        <p className="text-base font-bold text-gray-500">
                          {t('order_summary')}
                        </p>
                      </div>
                      <div className="flex w-full flex-row justify-between py-2">
                        <div>
                          <p className="text-gray-500">{t('tax_excluded')}</p>
                        </div>
                        <div>
                          <p className="text-black sm:mr-10">
                            {/*(totalPriceVAT - totalPrice).toFixed(2)*/}{' '}
                            {/*currency.toString()*/}
                          </p>
                        </div>
                      </div>
                      <hr className="h-0.5 w-full border border-gray-200" />

                      <div className="flex w-full flex-row justify-between py-2">
                        <div>
                          <p className="text-gray-500">{t('VAT')}</p>
                        </div>
                        <div>
                          <p className="text-black sm:mr-10">
                            {`${totalPrice.toFixed(2)} ${currency.toString()}`}
                          </p>
                        </div>
                      </div>
                      <hr className="h-0.5 w-full border border-gray-200" />

                      <div className="flex w-full flex-row justify-between py-2">
                        <div>
                          <p className="text-base font-bold text-gray-900">
                            {t('total')}
                          </p>
                        </div>
                        <div>
                          {(checkoutSession.checkout_session.cart
                            .original_price ?? 0) >
                          (checkoutSession.checkout_session.cart.total_price ??
                            0) ? (
                            <p className="font-bold text-black sm:mr-10">
                              <span className="mr-1 line-through">
                                {(checkoutSession.checkout_session.cart
                                  .original_price ?? 0) / 100}
                              </span>
                              {(
                                (checkoutSession.checkout_session.cart
                                  .total_price ?? 0) / 100
                              ).toFixed(2)}{' '}
                              {currency.toString()}
                            </p>
                          ) : (
                            <p className="font-bold text-black sm:mr-10">
                              {(checkoutSession.checkout_session.cart
                                .total_price ?? 0) / 100}{' '}
                              {currency.toString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
